import type { CartProductListItemDto } from '@repo/api/web/models'

import { useCartModule } from './useCartModule'

export interface SelectedChangePayload {
  id: number
  value: boolean
}

export const useCartSelection = () => {
  const { store } = useCartModule()

  const cartItems = computed(() => store.cart?.items || [])

  const availableCartItems = computed(() =>
    cartItems.value.filter(item => Boolean(item.inStock)),
  )
  const unavailableCartItems = computed(() =>
    cartItems.value.filter(item => !item?.inStock),
  )

  // Selection is per product line (by product id), not per unit — one CartItem row == one line.
  // Auto-select all available lines once when entering the cart view; later cart mutations
  // (qty, remove, restock) must not re-select items the user already deselected.
  const selectedProductIds = ref<number[]>([])
  let didApplyInitialSelection = false

  watch(
    cartItems,
    (newItems) => {
      const availableIds = newItems
        .filter(item => Boolean(item.inStock))
        .map(item => item.id!)
      const availableIdSet = new Set(availableIds)

      if (!didApplyInitialSelection) {
        // Wait until cart is loaded (null while plugins/root still fetching).
        if (store.cart == null) return

        selectedProductIds.value = availableIds
        didApplyInitialSelection = true
        return
      }

      selectedProductIds.value = selectedProductIds.value.filter(id =>
        availableIdSet.has(id),
      )
    },
    { immediate: true },
  )

  const selectedItems = computed<CartProductListItemDto[]>(() => {
    const idsSet = new Set(selectedProductIds.value)
    return availableCartItems.value.filter(item => idsSet.has(item.id!))
  })

  const allSelected = computed(
    () =>
      availableCartItems.value.length > 0
      && selectedProductIds.value.length >= availableCartItems.value.length,
  )

  const canCheckout = computed(() => selectedItems.value.length > 0)

  function toggleSelectAll() {
    selectedProductIds.value = allSelected.value
      ? []
      : availableCartItems.value.map(item => item.id!)
  }

  function handleSelectedChange(payload: SelectedChangePayload) {
    if (payload.value) {
      const item = cartItems.value.find(i => i.id === payload.id)
      if (!item || !item.inStock) return
      if (!selectedProductIds.value.includes(payload.id)) {
        selectedProductIds.value.push(payload.id)
      }
    } else {
      selectedProductIds.value = selectedProductIds.value.filter(
        id => id !== payload.id,
      )
    }
  }

  return {
    cartItems,
    availableCartItems,
    unavailableCartItems,
    selectedProductIds,
    selectedItems,
    allSelected,
    canCheckout,
    toggleSelectAll,
    handleSelectedChange,
  }
}
